import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE, formatApiError, formatCurrency, buildAssetUrl } from "@/lib/api";
import { staffApi, STAFF_TOKEN_KEY } from "@/lib/staffApi";
import { linkifyText } from "@/lib/linkify";
import { formatBookingStamp } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Wrench, LogOut, Loader2, Phone, MapPin, Clock, Coins, Camera,
  Route, AlertTriangle, ChevronDown, ChevronUp, Tag, Check,
  Wallet, Receipt, Plus, Trash2, Image as ImageIcon,
  Briefcase, Timer, MapPinned, FileText, User, RefreshCcw, HandCoins,
  CheckCircle2, Send, MailX, Package,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import BookingChat from "@/components/BookingChat";
import PaymentBadge from "@/components/PaymentBadge";
import StaffNotificationBell from "@/components/StaffNotificationBell";
import ConfirmDialog from "@/components/ConfirmDialog";
import { buildMapsUrl } from "@/lib/mapsUrl";

const PAYMENT_STATUSES = [
  { v: "awaiting_payment", label: "Awaiting payment" },
  { v: "paid", label: "Paid" },
  { v: "overdue", label: "Overdue" },
  { v: "write_off", label: "Write-off" },
  { v: "unable_to_collect", label: "Unable to collect" },
];

// Phase 22 — PaymentBadge + paymentBadgeVariant moved to
// `@/components/PaymentBadge` so the manager Calendar can reuse the exact
// same visual treatment. See `frontend/src/components/PaymentBadge.jsx`.

export function StaffPortalRouter() {
  // Decides login vs dashboard based on token presence
  const { slug } = useParams();
  const [stage, setStage] = useState("loading"); // loading | login | dash
  const [staff, setStaff] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem(STAFF_TOKEN_KEY);
    if (!token) {
      setStage("login");
      return;
    }
    staffApi.get("/staff-portal/me").then(({ data }) => {
      if (cancelled) return;
      if (data.slug !== slug) {
        // token belongs to a different staff member — force re-login
        localStorage.removeItem(STAFF_TOKEN_KEY);
        setStage("login");
      } else {
        setStaff(data);
        setStage("dash");
      }
    }).catch(() => {
      if (cancelled) return;
      localStorage.removeItem(STAFF_TOKEN_KEY);
      setStage("login");
    });
    return () => { cancelled = true; };
  }, [slug, navigate]);

  if (stage === "loading") {
    return <div className="min-h-screen grid place-items-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }
  if (stage === "login") {
    return <StaffLogin slug={slug} onLoggedIn={(me) => { setStaff(me); setStage("dash"); }} />;
  }
  return <StaffDashboard staff={staff} onLogout={() => { localStorage.removeItem(STAFF_TOKEN_KEY); setStage("login"); }} />;
}

function StaffLogin({ slug, onLoggedIn }) {
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/staff-portal/lookup/${slug}`).then(({ data }) => setName(data.name))
      .catch(() => setNotFound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{4}$/.test(pin)) {
      setError("PIN must be 4 digits");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/staff-portal/login`, { slug, pin });
      localStorage.setItem(STAFF_TOKEN_KEY, data.access_token);
      onLoggedIn(data.staff);
    } catch (e2) {
      setError(formatApiError(e2?.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6">
        <div className="bg-card border border-border rounded-[8px] p-8 max-w-sm text-center" data-testid="staff-not-found">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-rose-600">// Not found</div>
          <h2 className="text-xl font-extrabold mt-2">Unknown staff link</h2>
          <p className="text-sm text-muted-foreground mt-2">This portal URL is invalid or the staff member is inactive.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-primary text-primary-foreground p-6 relative">
      <div className="seam absolute top-0 inset-x-0" />
      <form onSubmit={submit} className="w-full max-w-sm bg-card text-foreground border border-border rounded-[8px] p-7 space-y-5 shadow-lg" data-testid="staff-login-form">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 grid place-items-center bg-accent text-accent-foreground rounded-[6px]">
            <Wrench className="h-5 w-5" />
          </div>
          <div>
            <div className="text-base font-extrabold tracking-tight">JobsBook</div>
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Staff portal</div>
          </div>
        </div>
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Welcome</div>
          <h2 className="text-2xl font-extrabold">{name || "Staff"}</h2>
          <p className="text-sm text-muted-foreground mt-1">Enter your 4-digit PIN to view today's jobs.</p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider font-semibold">PIN</Label>
          <Input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="••••"
            className="h-12 text-center mono text-2xl tracking-[0.5em]"
            autoFocus
            data-testid="staff-pin-input"
          />
        </div>
        {error && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-[6px] px-3 py-2" data-testid="staff-login-error">{error}</div>}
        <Button type="submit" disabled={submitting || pin.length !== 4} className="btn-cta w-full h-11" data-testid="staff-login-submit">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>
    </div>
  );
}

// Phase 32 — Eight-day rolling window: 2 days back · today · 5 days ahead.
// Returns ONLY days that have at least one job, with one exception: when
// today has no jobs we still return a placeholder section for today so
// the staff isn't confronted with a totally blank screen.
function buildEightDaySections(jobs) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dayKey = (d) => {
    const x = new Date(d); x.setHours(0, 0, 0, 0);
    return x.toISOString();
  };
  // Order: yesterday, day-before-yesterday at the top? No — chronological
  // ascending feels more natural for an 8-day list (matches the rest of the
  // app's calendar). We render the past sections WITH muted styling and the
  // future ones with bright headers; today gets the loudest header.
  const offsets = [-2, -1, 0, 1, 2, 3, 4, 5];
  const sections = offsets.map((o) => {
    const d = new Date(today); d.setDate(d.getDate() + o);
    return { offset: o, date: d, key: dayKey(d), jobs: [] };
  });
  for (const j of jobs) {
    const k = dayKey(new Date(j.scheduled_start));
    const s = sections.find((sec) => sec.key === k);
    if (s) s.jobs.push(j);
  }
  for (const s of sections) {
    s.jobs.sort((a, b) => a.scheduled_start.localeCompare(b.scheduled_start));
  }
  // Filter: only keep days with at least one booking. Exception: today
  // is ALWAYS kept (so we render the "No jobs today" placeholder).
  return sections.filter((sec) => sec.jobs.length > 0 || sec.offset === 0);
}

const WEEKDAY_NAMES = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY",
                      "THURSDAY", "FRIDAY", "SATURDAY"];
const MONTH_NAMES_SHORT = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN",
                          "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function offsetLabel(offset) {
  // Plain-English relative day used in test selectors and for accessibility.
  return ({
    "-2": "Two days ago", "-1": "Yesterday", "0": "Today",
    "1": "Tomorrow", "2": "Day after", "3": "Day 3 ahead",
    "4": "Day 4 ahead", "5": "Day 5 ahead",
  })[offset] || "";
}

function StaffDashboard({ staff, onLogout }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState(staff.currency || "EUR");
  const [requireLocation, setRequireLocation] = useState(staff.require_location !== false);
  const [companyName, setCompanyName] = useState(staff.company_name || "");
  // Phase 20 — booking_id → unread chat count from manager. Polled every 30s.
  const [unreadByBooking, setUnreadByBooking] = useState({});
  // Phase 21 — fields rendered on each summary card (manager-configurable).
  const [staffCardFields, setStaffCardFields] = useState(
    ["time", "customer_name", "address", "payment_form"]
  );
  // Phase P.29 — hide the "My stock" tab entirely when the staff has no
  // assigned stock rows. Recomputed on every load() so the tab reappears
  // the moment the manager hands out inventory.
  const [hasStock, setHasStock] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [j, me, ui, stock] = await Promise.all([
        staffApi.get("/staff-portal/jobs"),
        staffApi.get("/staff-portal/me").catch(() => null),
        staffApi.get("/staff-portal/ui-settings").catch(() => null),
        staffApi.get("/staff-portal/my-stock").catch(() => ({ data: [] })),
      ]);
      setJobs(j.data);
      setHasStock(Array.isArray(stock?.data) && stock.data.length > 0);
      if (me?.data) {
        setCurrency(me.data.currency || "EUR");
        setRequireLocation(me.data.require_location !== false);
        setCompanyName(me.data.company_name || "");
      }
      const fields = ui?.data?.staff_card_fields;
      if (Array.isArray(fields) && fields.length > 0) {
        setStaffCardFields(fields);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 20 — poll unread chat counts every 30s. Light, silent.
  useEffect(() => {
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const { data } = await staffApi.get("/staff-portal/bookings/unread-count");
        if (!cancelled) setUnreadByBooking(data || {});
      } catch { /* swallow */ }
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const sections = buildEightDaySections(jobs);

  // Phase P.28 — staff portal palette. Cyber Teal + Aqua Foam replace the
  // manager-side amber. Kept as scoped constants so the manager UI is
  // unaffected. `pb-24 sm:pb-12` reserves room for the bottom-tab bar on
  // mobile viewports.
  const P28_TEAL = "#03313A";
  const P28_FOAM = "#8FFFE0";
  const [tab, setTab] = useState("jobs");

  return (
    <div className="min-h-screen bg-[#F4F7F8] pb-24 sm:pb-12">
      <div className="seam" />
      {/* Phase P.28 — header repainted Cyber Teal. */}
      <header
        className="sticky top-0 z-50 shadow-lg"
        style={{ backgroundColor: P28_TEAL, color: "#fff" }}
        data-testid="staff-header"
      >
        <div className="max-w-[800px] mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="h-11 w-11 grid place-items-center rounded-2xl shrink-0"
              style={{ backgroundColor: P28_FOAM, color: P28_TEAL }}
            >
              <Wrench className="h-5 w-5" />
            </div>
            <div className="leading-tight min-w-0">
              <div
                className="font-extrabold tracking-tight truncate text-[15px] sm:text-base"
                data-testid="staff-header-company-name"
                title={companyName}
              >
                {companyName || "Staff portal"}
              </div>
              <div className="mono text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-white/70 truncate">
                {staff?.name || "Staff"} · 8-day rolling
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="staff-refresh"
              className="bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white hover:border-white/60 font-medium min-h-[44px] min-w-[44px] px-2 sm:px-3 disabled:opacity-60 rounded-xl"
              aria-label="Refresh"
              title="Refresh jobs"
            >
              <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""} sm:mr-1.5`} aria-hidden="true" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <StaffNotificationBell onEvent={load} />
            <Button
              variant="outline"
              size="sm"
              onClick={onLogout}
              data-testid="staff-sign-out"
              className="bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white hover:border-white/60 font-medium min-h-[44px] min-w-[44px] px-2 sm:px-3 rounded-xl"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="h-4 w-4 sm:mr-1.5" aria-hidden="true" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[800px] mx-auto px-4 py-6">
        {loading ? (
          <div className="text-muted-foreground text-sm">Loading…</div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="space-y-4">
            {/* Phase P.28 — desktop top-tabs stay, mobile gets a bottom
                fixed nav bar with the same three destinations. */}
            <TabsList
              className="hidden sm:inline-flex bg-white border border-[#03313A]/10 rounded-2xl p-1 shadow-sm"
              data-testid="staff-tabs"
            >
              <TabsTrigger
                value="jobs"
                data-testid="staff-tab-jobs"
                className="rounded-xl data-[state=active]:bg-[#03313A] data-[state=active]:text-white px-4 py-2 font-bold"
              >
                Jobs
              </TabsTrigger>
              {hasStock && (
                <TabsTrigger
                  value="stock"
                  data-testid="staff-tab-stock"
                  className="rounded-xl data-[state=active]:bg-[#03313A] data-[state=active]:text-white px-4 py-2 font-bold"
                >
                  My stock
                </TabsTrigger>
              )}
              <TabsTrigger
                value="ledger"
                data-testid="staff-tab-ledger"
                className="rounded-xl data-[state=active]:bg-[#03313A] data-[state=active]:text-white px-4 py-2 font-bold"
              >
                My ledger
              </TabsTrigger>
            </TabsList>

            <TabsContent value="jobs">
              <div className="space-y-6">
                {sections.map((sec) => {
                  const isToday = sec.offset === 0;
                  const isPast = sec.offset < 0;
                  const slug = offsetLabel(sec.offset).toLowerCase().replace(/\s+/g, "-");
                  return (
                    <section key={sec.key} data-testid={`staff-day-${slug}`}>
                      <StaffDayHeader date={sec.date} offset={sec.offset} />
                      <StaffDayMileagePill date={sec.date} hasJobs={sec.jobs.length > 0} />
                      {sec.jobs.length === 0 ? (
                        // Today is the only section that survives empty —
                        // we explicitly render a friendly placeholder so the
                        // staff sees a confirmation that there's nothing on
                        // today rather than a blank screen. Past / future
                        // empty days are filtered out by `buildEightDaySections`.
                        isToday ? (
                          <div
                            className="text-sm text-muted-foreground italic bg-secondary/30 border border-dashed border-border rounded-[6px] px-4 py-6 text-center"
                            data-testid="staff-no-jobs-today"
                          >
                            No jobs today — enjoy the quiet.
                          </div>
                        ) : null
                      ) : (
                        <div className="space-y-2">
                          {sec.jobs.map((j, idx) => {
                            // Phase 32a — render a small "Route" pill BETWEEN
                            // consecutive cards (same day, same staff), matching
                            // the manager calendar TravelPill aesthetic.
                            const showPill = idx > 0 && j.travel_from_previous;
                            const prev = idx > 0 ? sec.jobs[idx - 1] : null;
                            return (
                              <div key={j.id} className="space-y-2">
                                {showPill && (
                                  <StaffRoutePill
                                    travel={j.travel_from_previous}
                                    prevJob={prev}
                                    nextJob={j}
                                  />
                                )}
                                <StaffJobCard
                                  job={j}
                                  currency={currency}
                                  requireLocation={requireLocation}
                                  companyName={companyName}
                                  unreadCount={unreadByBooking[j.id] || 0}
                                  cardFields={staffCardFields}
                                  onChanged={load}
                                  isPastDay={isPast}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="stock">
              <StaffMyStockPanel />
            </TabsContent>

            <TabsContent value="ledger">
              <StaffMyLedger currency={currency} />
            </TabsContent>
          </Tabs>
        )}
      </main>

      {/* Phase P.28 — mobile bottom-tab bar. Visible only on <sm viewports;
          desktop keeps the top-tab TabsList. Uses the exact same `tab`
          state so switching keeps in sync. */}
      {!loading && (
        <nav
          className="sm:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[#03313A]/10 bg-white shadow-[0_-6px_24px_rgba(3,49,58,0.08)] pb-[env(safe-area-inset-bottom)]"
          data-testid="staff-bottom-nav"
        >
          <div className={`max-w-[800px] mx-auto grid ${hasStock ? "grid-cols-3" : "grid-cols-2"}`}>
            {[
              { key: "jobs", label: "Jobs", Icon: Briefcase, show: true },
              { key: "stock", label: "My stock", Icon: Package, show: hasStock },
              { key: "ledger", label: "My ledger", Icon: Wallet, show: true },
            ].filter((t) => t.show).map(({ key, label, Icon }) => {
              const active = tab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  data-testid={`staff-bottom-tab-${key}`}
                  className={`flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-bold ${active ? "text-[#03313A]" : "text-muted-foreground"}`}
                >
                  <Icon className={`h-5 w-5 ${active ? "text-[#03313A]" : ""}`} />
                  <span>{label}</span>
                  {active && <span className="mt-0.5 h-0.5 w-6 rounded-full bg-[#8FFFE0]" />}
                </button>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}

// Phase 13: staff portal ledger tab
function StaffMyLedger({ currency }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [adding, setAdding] = useState(null); // 'expense' | 'cash_intake' | null

  const load = async () => {
    try {
      const [e, s] = await Promise.all([
        staffApi.get("/staff-portal/ledger"),
        staffApi.get("/staff-portal/ledger/summary"),
      ]);
      setEntries(e.data);
      setSummary(s.data);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (entry) => {
    if (!confirm("Delete this ledger entry?")) return;
    try {
      await staffApi.delete(`/staff-portal/ledger/${entry.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  // Phase P.27.2 — align the staff-portal "holding" card with the manager
  // ledger view (both must show UNSETTLED-only totals; settled entries roll
  // out of the "currently holding" number by definition). Backend returns
  // both aggregates so we pick the outstanding_* ones here. Fallback to
  // the legacy fields for backward-compat with older server responses.
  const outstandingSubtotals = summary?.outstanding_subtotals ?? summary?.subtotals ?? {};
  const outstandingBalance = (summary?.outstanding_balance ?? summary?.net_amount_owed_to_staff) ?? 0;
  const net = outstandingBalance;
  const tone = Math.abs(net) < 0.005 ? "settled" : net > 0 ? "owed-to-staff" : "owed-from-staff";
  const toneCopy = tone === "owed-to-staff" ? "Company owes you" : tone === "owed-from-staff" ? "You're holding" : "Settled";

  return (
    <div className="space-y-4" data-testid="staff-portal-ledger">
      {summary && (
        <div
          className={`rounded-2xl border-2 p-5 shadow-sm ${tone === "settled" ? "border-stone-300 bg-white" : tone === "owed-to-staff" ? "border-emerald-300 bg-emerald-50" : "border-[#8FFFE0] bg-[#F0FFFA]"}`}
          data-testid="staff-portal-ledger-card"
        >
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{toneCopy}</div>
          <div
            className={`text-4xl font-black mt-1 tabular-nums ${tone === "owed-to-staff" ? "text-emerald-800" : tone === "owed-from-staff" ? "text-[#03313A]" : ""}`}
            data-testid="staff-portal-ledger-net"
          >
            {tone === "settled" ? "—" : formatCurrency(Math.abs(net), currency)}
          </div>
          <div className="text-xs text-muted-foreground mt-2 mono flex flex-wrap gap-x-3 gap-y-1">
            <span>Intake {formatCurrency(outstandingSubtotals.cash_intake || 0, currency)}</span>
            <span>Expense {formatCurrency(outstandingSubtotals.expense || 0, currency)}</span>
            <span>Payout {formatCurrency(outstandingSubtotals.payout || 0, currency)}</span>
            <span>Returned {formatCurrency(outstandingSubtotals.cash_collected_from_staff || 0, currency)}</span>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          onClick={() => setAdding("expense")}
          className="flex-1 min-h-[48px] rounded-xl text-white font-bold"
          style={{ backgroundColor: "#03313A" }}
          data-testid="ledger-add-expense"
        >
          <Receipt className="h-4 w-4 mr-1.5" /> Log expense
        </Button>
        <Button
          onClick={() => setAdding("cash_intake")}
          variant="outline"
          className="flex-1 min-h-[48px] rounded-xl border-[#03313A]/20 text-[#03313A] hover:bg-[#8FFFE0]/25 font-bold"
          data-testid="ledger-add-intake"
        >
          <Wallet className="h-4 w-4 mr-1.5" /> Log cash intake
        </Button>
      </div>

      <div className="space-y-2">
        {entries.map((e, idx) => {
          // Phase P.29 — settlement boundary divider. Entries come sorted
          // newest → oldest, so a divider goes above the FIRST entry of
          // each settled batch (i.e. when moving from a row without a
          // `settlement_id` — or with a different one — into a settled
          // batch). Purely visual: no data mutation.
          const prev = entries[idx - 1];
          const showSettleDivider = e.settlement_id && (!prev || prev.settlement_id !== e.settlement_id);
          // Phase P.27.2 — sign matches the STAFF WALLET perspective (not
          // the net-owed-to-staff ledger sign). Money flowing INTO the
          // staff's hands (`cash_intake` = they now hold company cash;
          // `payout` = they received a reimbursement) renders as `+`;
          // money flowing OUT (`expense` = out-of-pocket spend they'll be
          // reimbursed for; `cash_collected_from_staff` = returned cash)
          // renders as `−`. This kills the "Cash intake shown as -€500"
          // confusion the user reported without changing DB signs.
          const sign = e.entry_type === "cash_intake" || e.entry_type === "payout" ? "+" : "−";
          const colour = e.entry_type === "expense" ? "text-emerald-700"
                       : e.entry_type === "cash_intake" ? "text-amber-700"
                       : e.entry_type === "payout" ? "text-rose-700"
                       : "text-sky-700";
          const label = { cash_intake: "Cash intake", expense: "Expense", payout: "Payout", cash_collected_from_staff: "Cash returned" }[e.entry_type] || e.entry_type;
          return (
            <React.Fragment key={e.id}>
              {showSettleDivider && (
                <div
                  className="flex items-center gap-3 py-2"
                  data-testid={`ledger-settle-divider-${e.settlement_id}`}
                >
                  <div className="h-[2px] flex-1 bg-[#03313A]" />
                  <span className="mono text-[10px] uppercase tracking-widest text-[#03313A] font-bold whitespace-nowrap">
                    Settled {(e.entry_date || "").slice(0, 10)}
                  </span>
                  <div className="h-[2px] flex-1 bg-[#03313A]" />
                </div>
              )}
              <div className="bg-white border border-[#03313A]/10 rounded-2xl p-4 flex items-start justify-between gap-3 shadow-sm" data-testid={`ledger-entry-${e.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-bold ${colour}`}>{label}</span>
                  {e.auto_generated && <span className="text-[10px] mono uppercase bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded">Auto</span>}
                  <span className="mono text-[11px] text-muted-foreground">{(e.entry_date || "").slice(0, 10)}</span>
                </div>
                {e.note && <div className="text-sm mt-0.5">{e.note}</div>}
                {e.booking_code && <div className="text-[11px] mono text-muted-foreground">{e.booking_code}{e.linked_invoice_number ? ` → ${e.linked_invoice_number}` : ""}</div>}
                {e.photo_path && (
                  <a href={e.photo_path} target="_blank" rel="noreferrer" className="text-[11px] text-accent hover:underline inline-flex items-center gap-1 mt-0.5">
                    <ImageIcon className="h-3 w-3" /> Photo
                  </a>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className={`mono font-bold ${colour}`}>{sign}{formatCurrency(Number(e.amount || 0), e.currency || currency)}</div>
                {!e.auto_generated && (e.entry_type === "expense" || e.entry_type === "cash_intake") && (
                  <Button size="sm" variant="ghost" className="text-rose-700 hover:text-rose-900 -mr-2" onClick={() => remove(e)} data-testid={`ledger-entry-delete-${e.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
            </React.Fragment>
          );
        })}
        {entries.length === 0 && <div className="text-center text-sm text-muted-foreground py-6">No entries yet — log an expense or cash intake to get started.</div>}
      </div>

      {adding && <StaffLedgerCreateDialog kind={adding} onClose={() => setAdding(null)} onSaved={() => { setAdding(null); load(); }} />}
    </div>
  );
}

function StaffLedgerCreateDialog({ kind, onClose, onSaved }) {
  const [form, setForm] = useState({ amount: "", note: "", entry_date: new Date().toISOString().slice(0, 10) });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!Number(form.amount) || Number(form.amount) <= 0) { toast.error("Enter a positive amount"); return; }
    setBusy(true);
    try {
      const { data } = await staffApi.post("/staff-portal/ledger", {
        entry_type: kind,
        amount: Number(form.amount),
        note: form.note,
        entry_date: form.entry_date ? `${form.entry_date}T12:00:00Z` : null,
      });
      if (kind === "expense" && photo) {
        const fd = new FormData(); fd.append("file", photo);
        try {
          await staffApi.post(`/staff-portal/ledger/${data.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (e) { toast.error(`Saved entry but photo upload failed: ${formatApiError(e?.response?.data?.detail)}`); }
      }
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const isExp = kind === "expense";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="staff-ledger-create-dialog">
        <DialogHeader>
          <DialogTitle>{isExp ? "Log expense" : "Log cash intake"}</DialogTitle>
          <DialogDescription>{isExp ? "An expense you paid out-of-pocket that the company should reimburse." : "Cash you received on behalf of the company (e.g. from a customer)."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Amount (€)</Label>
            <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="ledger-entry-amount" />
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} />
          </div>
          <div>
            <Label>Note</Label>
            <Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={isExp ? "diesel, parts, parking…" : "from customer, job code…"} />
          </div>
          {isExp && (
            <div>
              <Label>Receipt photo (optional)</Label>
              <Input type="file" accept="image/*,.pdf,.heic" onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="ledger-entry-photo" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} onClick={submit} data-testid="staff-ledger-create-submit">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Phase 21 — Per-row renderer for the staff job summary card. Manager picks
// the order via Settings; we hide rows whose value is empty rather than show
// blanks. Address/postcode rows also respect `requireLocation`.
function StaffCardFields({ job, fields, requireLocation }) {
  const loc = job.location || {};
  const templateName =
    (job.line_items && job.line_items.length > 0)
      ? `${job.line_items[0].description}${job.line_items.length > 1 ? ` +${job.line_items.length - 1}` : ""}`
      : (job.template_snapshot?.name || "");
  const notesPreview = (job.notes || "").trim();
  const truncated = notesPreview.length > 80 ? `${notesPreview.slice(0, 80)}…` : notesPreview;

  const ROW_DEFS = {
    time: {
      // The compact card already shows time in the top-right header, so when
      // the manager picks "time" we render a labelled time row inside the body
      // to keep the chosen ordering meaningful (avoids the duplicate-row trap
      // by labelling it). Skipped if user puts it first AND the header above
      // is enough — but spec says to always honour user's chosen order, so
      // we keep the row.
      icon: Clock,
      label: "Time",
      content: new Date(job.scheduled_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    },
    customer_name: {
      icon: User,
      label: "Customer",
      content: job.customer_name || "",
      strong: true,
    },
    address: {
      icon: MapPin,
      label: "Address",
      content: requireLocation ? (loc.address_line || "") : "",
      truncate: true,
    },
    payment_form: {
      icon: Coins,
      label: "Payment",
      content: (job.payment_type_name || job.payment_type_label || job.payment_type || "")
        .toString()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase()),
    },
    job_template: {
      icon: Briefcase,
      label: "Job",
      content: templateName,
      truncate: true,
    },
    duration: {
      icon: Timer,
      label: "Duration",
      content: job.scheduled_duration_minutes ? `${job.scheduled_duration_minutes} min` : "",
    },
    phone: {
      icon: Phone,
      label: "Phone",
      content: job.customer_phone || "",
    },
    postcode: {
      icon: MapPinned,
      label: "Postcode",
      content: requireLocation ? (loc.postcode || "") : "",
    },
    notes_preview: {
      icon: FileText,
      label: "Notes",
      content: truncated,
      truncate: true,
    },
  };

  return (
    <div className="mt-1.5 space-y-1" data-testid={`staff-job-card-fields-${job.id}`}>
      {fields.map((key) => {
        // Phase 21.1 — `payment_form` is now rendered as an always-visible
        // badge by `PaymentBadge` in the card header. Suppress the configurable
        // row to avoid double-rendering. We still accept the key in the
        // settings list (backward compat for managers who already saved it).
        if (key === "payment_form") return null;
        const def = ROW_DEFS[key];
        if (!def) return null;
        const v = (def.content || "").toString().trim();
        if (!v) return null; // hide empty rows
        const Icon = def.icon;
        return (
          <div
            key={key}
            className={`flex items-center gap-2 text-xs ${def.strong ? "font-bold text-base text-foreground" : "text-muted-foreground"}`}
            data-testid={`staff-job-card-row-${key}-${job.id}`}
          >
            <Icon className={`shrink-0 ${def.strong ? "h-4 w-4" : "h-3 w-3"}`} />
            <span className={`${def.truncate ? "truncate" : ""}`}>{v}</span>
          </div>
        );
      })}
    </div>
  );
}


// Phase 32a — Route pill rendered BETWEEN two consecutive staff job cards
// on the same day. Visually consistent with the manager calendar's
// TravelPill (sky-tinted, rounded-full, mono uppercase). Tap/hover shows
// the source address → destination address tooltip.
function StaffRoutePill({ travel, prevJob, nextJob }) {
  if (!travel) return null;
  const prevAddr =
    prevJob?.location?.address_line ||
    prevJob?.location?.postcode ||
    "previous job";
  const nextAddr =
    nextJob?.location?.address_line ||
    nextJob?.location?.postcode ||
    "this job";
  const minutes = Math.round(Number(travel.duration_min || 0));
  const km = Number(travel.distance_km || 0);
  return (
    <div
      className="flex items-center gap-1.5 -my-0.5 select-none px-1"
      title={`${prevAddr} → ${nextAddr}`}
      data-testid={nextJob ? `staff-route-pill-${nextJob.id}` : undefined}
    >
      <div className="h-3 w-px bg-sky-300 ml-3" aria-hidden="true" />
      <span className="inline-flex items-center gap-1 text-[10px] mono uppercase tracking-wider text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5">
        <Route className="h-3 w-3" />
        <span aria-hidden="true">↗</span>
        {minutes} min · {km} km
      </span>
    </div>
  );
}




// =====================================================================
// Phase 32 — Polished, gradient sticky day headers.
//   Past days   (-2, -1)  : slate-600 → slate-700, no badge
//   Today       (0)       : indigo-600 → fuchsia-500 with a "TODAY" pill
//   Future days (+1..+5)  : emerald-500 → teal-600, no badge
// Sticky positioning (sticky top-0 z-20 inside the day section) keeps
// the colour band visible as the staff scrolls through that day's cards.
// Mobile-responsive: text-base on narrow viewports, text-lg ≥ sm.
// =====================================================================
function StaffDayMileagePill({ date, hasJobs }) {
  // Phase 43 — small daily mileage pill under each day header. Fires
  // `/staff-portal/daily-mileage` once per (staff, day). Shows nothing
  // when the staff has no base coords OR the day has zero bookings.
  const [snap, setSnap] = useState(null);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!hasJobs) { setSnap(null); return; }
    const dateStr = date.toISOString().slice(0, 10);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await staffApi.get(`/staff-portal/daily-mileage?date=${dateStr}`);
        if (!cancelled) setSnap(data);
      } catch {
        if (!cancelled) setSnap(null);
      }
    })();
    return () => { cancelled = true; };
  }, [date, hasJobs]);

  if (!snap || !snap.has_base || !snap.legs || snap.legs.length === 0) return null;
  const km = Number(snap.total_km || 0).toFixed(1);
  const mins = Math.round(Number(snap.total_minutes || 0));
  const hours = Math.floor(mins / 60);
  const remMin = mins - hours * 60;
  const durLabel = hours > 0 ? `${hours}h ${String(remMin).padStart(2, "0")}m` : `${remMin}m`;

  // Leg kind → friendly label. The backend returns booking codes (e.g.
  // B-000123) or the literal "home"; we polish those into readable
  // copy for the breakdown drop-down.
  const legLabel = (leg) => {
    if (leg.kind === "home_to_first") return `Home → ${leg.to}`;
    if (leg.kind === "last_to_home") return `${leg.from} → Home`;
    return `${leg.from} → ${leg.to}`;
  };

  return (
    <div className="mb-2 mt-0">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-900 text-[11px] font-bold px-2.5 py-1 mono tracking-wider transition-colors"
        data-testid="staff-day-mileage-pill"
        aria-expanded={expanded}
      >
        <Route className="h-3 w-3" />
        {km} km · {durLabel}
        <span className="text-[9px] opacity-70 ml-1">{expanded ? "▲" : "▼"}</span>
      </button>
      {expanded && (
        <div className="mt-1.5 border border-amber-200 bg-amber-50/60 rounded-md p-2 text-[11px] text-amber-900 space-y-0.5"
             data-testid="staff-day-mileage-breakdown">
          {snap.legs.map((leg, i) => (
            <div key={i} className="flex items-center gap-2 mono">
              <span className="opacity-60 w-3 text-center">{i + 1}.</span>
              <span className="flex-1 truncate">{legLabel(leg)}</span>
              <span className="font-bold tabular-nums">{Number(leg.km).toFixed(1)} km</span>
              <span className="opacity-70 tabular-nums w-12 text-right">{Math.round(leg.minutes)} min</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function StaffDayHeader({ date, offset }) {
  const isToday = offset === 0;
  const isPast = offset < 0;
  const weekdayName = WEEKDAY_NAMES[date.getDay()];
  const dayNum = String(date.getDate());
  const monthShort = MONTH_NAMES_SHORT[date.getMonth()];
  const grad = isToday
    ? "from-indigo-600 via-purple-600 to-fuchsia-500"
    : isPast
    ? "from-slate-600 to-slate-700"
    : "from-emerald-500 to-teal-600";
  return (
    <div
      className={`sticky top-0 z-20 mb-3 -mx-1 sm:mx-0 rounded-md overflow-hidden bg-gradient-to-r ${grad} shadow-sm`}
      data-testid={`staff-day-header-${offset}`}
      data-today={isToday ? "true" : "false"}
    >
      <div className="flex items-baseline justify-between px-4 py-2.5 text-white">
        <div className="flex items-baseline gap-2.5 min-w-0">
          <span
            className="font-extrabold text-base sm:text-lg tracking-wide mono uppercase truncate"
            data-testid={`staff-day-header-label-${offset}`}
          >
            {weekdayName}
            <span className="font-normal opacity-80"> · </span>
            <span className="font-extrabold">{dayNum} {monthShort}</span>
          </span>
        </div>
        {isToday && (
          <span
            className="text-[10px] font-extrabold mono uppercase tracking-[0.2em] bg-white/95 text-fuchsia-700 rounded-full px-2.5 py-0.5 shadow-sm"
            data-testid="staff-day-header-today-pill"
          >
            TODAY
          </span>
        )}
      </div>
    </div>
  );
}


// =====================================================================
// Phase 32 — Mark-as-Paid modal + pending pill rendered inside each
// staff job card. Visible only when the job's payment_status is
// `awaiting_payment` or `overdue`. After successful submit the card
// flips to "Awaiting approval" until the manager acts.
// =====================================================================
function MarkPaidDialog({ job, currency, open, onOpenChange, onMarked }) {
  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState(
    job?.total != null ? String(job.total) : ""
  );
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = {
        method,
        amount: amount === "" ? null : Number(amount),
        note: note.trim() || null,
      };
      const { data } = await staffApi.post(
        `/staff-portal/bookings/${job.id}/mark-paid`, body
      );
      toast.success("Submitted for manager approval");
      onMarked?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not mark as paid");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mark-paid-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Mark as Paid</DialogTitle>
          <DialogDescription>
            Submit this job for manager approval. The booking will move to
            <span className="font-semibold"> Awaiting approval</span> until
            the manager confirms.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-11" data-testid="mark-paid-method-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="transfer">Bank transfer</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">
              Amount ({currency || "EUR"})
              <span className="text-muted-foreground ml-1 normal-case font-normal">
                — leave blank to use invoice total
              </span>
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={job?.total != null ? `${job.total}` : "0.00"}
              data-testid="mark-paid-amount-input"
              className="h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Note (optional)</Label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Any context for the manager…"
              data-testid="mark-paid-note-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="mark-paid-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="mark-paid-confirm">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AwaitingApprovalPill({ approval }) {
  if (!approval) return null;
  const since = (() => {
    try {
      const diffMin = Math.round((Date.now() - new Date(approval.marked_at).getTime()) / 60000);
      if (diffMin < 1) return "just now";
      if (diffMin < 60) return `${diffMin} min ago`;
      const h = Math.floor(diffMin / 60);
      return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
    } catch {
      return "recently";
    }
  })();
  return (
    <div
      className="mt-2 inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2.5 py-0.5"
      title={`Submitted ${since} — waiting for manager approval`}
      data-testid={`awaiting-approval-pill-${approval.booking_id}`}
    >
      <HandCoins className="h-3 w-3" />
      Awaiting approval · {since}
    </div>
  );
}




function StaffJobCard({ job, currency, requireLocation = true, companyName = "", unreadCount = 0, cardFields, onChanged, isPastDay = false }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(job.staff_notes || "");
  const [paymentStatus, setPaymentStatus] = useState(job.payment_status);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeNote, setScopeNote] = useState("");
  const [submittingScope, setSubmittingScope] = useState(false);
  // Phase 32 — mark-paid modal + latest approval (pending OR
  // most-recent rejected) attached to this job.
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [latestApproval, setLatestApproval] = useState(null);
  // Phase P.11 — Mark Completed flow state.
  const [completing, setCompleting] = useState(false);
  const [postCompletePrompt, setPostCompletePrompt] = useState(null);
  // Phase P.15 — products & template proposal dialogs.
  const [productsOpen, setProductsOpen] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  // Phase P.17 — "Add extra service" (append existing template) dialog.
  const [addExtraOpen, setAddExtraOpen] = useState(false);
  // Phase P.16 — pre-complete flow ("products" → "signature" → done).
  const [preCompleteStep, setPreCompleteStep] = useState(null);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const fileRef = useRef(null);

  // Pull the latest approval row when the job is pending OR was rejected.
  // We bail out for fully-paid jobs to keep the round-trips minimal.
  useEffect(() => {
    if (job.payment_status !== "pending_manager_approval"
        && job.payment_status !== "awaiting_payment") {
      setLatestApproval(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await staffApi.get(`/staff-portal/bookings/${job.id}/pending-approval`);
        if (!cancelled) setLatestApproval(data || null);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [job.id, job.payment_status]);

  const loc = job.location || {};
  const postcode = loc.postcode || loc.eircode || "";
  // Phase 50.3 — use the shared helper which respects manager-supplied
  // location_url first, then lat/lng coords, then falls back to a
  // clean URL-encoded address join. Fixes the "no results" bug where
  // we were always rebuilding from raw text even when good coords
  // existed on the booking.
  const mapsHref = buildMapsUrl(loc);

  const saveNotes = async () => {
    if (notes === (job.staff_notes || "")) return;
    try {
      await staffApi.patch(`/staff-portal/jobs/${job.id}`, { staff_notes: notes });
      toast.success("Notes saved");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const updatePayment = async (v) => {
    setPaymentStatus(v);
    try {
      await staffApi.patch(`/staff-portal/jobs/${job.id}`, { payment_status: v });
      toast.success("Payment status updated");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await staffApi.post(`/staff-portal/jobs/${job.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const flagScope = async () => {
    if (!scopeNote.trim()) { toast.error("Note is required"); return; }
    setSubmittingScope(true);
    try {
      await staffApi.post(`/staff-portal/jobs/${job.id}/flag-scope-change`, { note: scopeNote.trim() });
      toast.success("Scope change flagged");
      setScopeNote("");
      setScopeOpen(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSubmittingScope(false); }
  };

  // Phase P.11 — Staff "Mark Completed" flow. Server returns a
  // `prompt_send_invoice` hint so we only surface the "Send invoice?"
  // popup for VAT/RCT-style bookings (cash flows are untouched).
  //
  // Bug fix (P.11.1): the prior version called `onChanged?.()` immediately
  // after the API call, which triggers the parent `load()`. On tabs that
  // filter out completed jobs the parent then omits this booking from
  // `sec.jobs`, which unmounts <StaffJobCard>. The subsequent
  // `setPostCompletePrompt({...})` state update lands on an unmounted
  // component, so the Dialog never opens. Fix: when the prompt should
  // fire, set state FIRST and DEFER `onChanged` until the popup closes.
  const markCompleted = async () => {
    if (completing) return;
    // Phase P.16 — pre-complete probe: decide which dialogs to show.
    try {
      const { data: probe } = await staffApi.get(
        `/staff-portal/bookings/${job.id}/pre-complete-check`,
      );
      // Step 1: products reminder (only if tenant setting ON AND staff has stock).
      if (probe?.prompt_products && probe?.has_stock) {
        setPreCompleteStep("products");
        return;
      }
      // Step 2: signature (only if tenant setting ON AND not already captured).
      if (probe?.prompt_signature && !probe?.signature_present) {
        setPreCompleteStep("signature");
        return;
      }
    } catch (_) {
      // If probe fails, fall through to the original completion — never
      // block the primary action because of a diagnostic call.
    }
    await _doFinalComplete();
  };

  // Phase P.16 — the final leg (unchanged from P.11) — runs after the two
  // optional pre-complete popups have closed.
  const _doFinalComplete = async () => {
    setCompleting(true);
    try {
      const { data } = await staffApi.post(`/staff-portal/bookings/${job.id}/complete`);
      if (data?.prompt_send_invoice) {
        setPostCompletePrompt({ payment_type_name: data.payment_type_name || "" });
        toast.success("Job marked completed — send invoice?");
      } else {
        toast.success("Job marked completed");
        onChanged?.();
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setCompleting(false);
    }
  };

  // "Later" button in the popup: no send, just close + refresh parent.
  const dismissPromptLater = () => {
    setPostCompletePrompt(null);
    onChanged?.();
  };

  const sendInvoiceNow = async () => {
    if (sendingInvoice) return;
    setSendingInvoice(true);
    try {
      const { data } = await staffApi.post(`/staff-portal/bookings/${job.id}/send-invoice`);
      if (data?.sent) {
        toast.success("Invoice sent to the customer");
      } else if (data?.warning === "no_customer_email") {
        toast.error("Invoice not sent — customer has no email on file. The manager has been flagged.");
      } else {
        toast.success("Invoice send completed");
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSendingInvoice(false);
      setPostCompletePrompt(null);
      onChanged?.();
    }
  };

  // Phase P.28 — mobile-app style job card. Cyber Teal + Aqua Foam palette,
  // rounded-2xl shell, generous padding, BIG BOLD time-range at the top.
  const startDt = new Date(job.scheduled_start);
  const endDt = job.scheduled_duration_minutes
    ? new Date(startDt.getTime() + Number(job.scheduled_duration_minutes) * 60000)
    : null;
  const fmtHm = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const timeRange = endDt ? `${fmtHm(startDt)}–${fmtHm(endDt)}` : fmtHm(startDt);

  return (
    <div
      className="bg-white border border-[#03313A]/10 rounded-2xl relative transition-shadow shadow-sm hover:shadow-md overflow-hidden"
      data-testid={`staff-job-${job.id}`}
      data-staff-job-id={job.id}
      id={`staff-job-card-${job.id}`}
    >
      {unreadCount > 0 && (
        <span
          className="absolute top-3 right-3 min-w-[20px] h-[20px] px-1.5 rounded-full bg-rose-600 text-[11px] font-bold text-white flex items-center justify-center ring-2 ring-white z-10"
          data-testid={`staff-chat-unread-badge-${job.id}`}
          aria-label={`${unreadCount} unread chat message${unreadCount > 1 ? "s" : ""}`}
        >
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
      <button type="button" onClick={() => setExpanded((s) => !s)} className="w-full text-left">
        {/* TOP BLOCK — big time · grand total · payment pill · booking code */}
        <div className="px-5 pt-4 pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div
                className="text-3xl sm:text-[34px] font-black tracking-tight leading-none text-[#03313A] tabular-nums"
                data-testid={`staff-job-time-${job.id}`}
              >
                {timeRange}
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1.5">
                {job.booking_code}
              </div>
            </div>
            <div className="shrink-0 mr-8 flex flex-col items-end gap-1.5">
              {/* Phase P.32 — Grand total (incl. VAT) prominent under the
                  payment pill, matching the time-range font weight so it
                  reads as equally important on the compact card. */}
              <PaymentBadge job={job} size="sm" />
              <div
                className="text-right"
                data-testid={`staff-job-grand-total-${job.id}`}
              >
                <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground leading-none">
                  Collect
                </div>
                <div className="text-2xl sm:text-[26px] font-black tabular-nums text-[#03313A] leading-none mt-1 mono">
                  {formatCurrency(
                    job.total_incl_vat != null ? job.total_incl_vat : (job.subtotal || 0),
                    currency,
                  )}
                </div>
                <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground/80 mt-0.5">
                  incl. VAT
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* MIDDLE BLOCK — customer + address (dynamic fields, minus time which is
            already rendered above). */}
        <div className="px-5 py-3 border-t border-[#03313A]/5 bg-[#F4F7F8]/40">
          <StaffCardFields
            job={job}
            fields={(cardFields || ["time", "customer_name", "address", "payment_form"]).filter((k) => k !== "time")}
            requireLocation={requireLocation}
          />
        </div>

        {/* BOTTOM BLOCK — actions & meta */}
        <div className="px-5 pt-3 pb-4">
        {/* Phase 32a — in-card travel chip removed. Travel is now rendered
            as a `<StaffRoutePill>` BETWEEN consecutive same-day cards in
            the parent loop, matching the manager calendar TravelPill. */}
        {/* Phase 32 — Mark-as-Paid CTA + workflow state. Visible only when
            payment is open and the day is not already in the past (past
            days are read-only context). */}
        {!isPastDay && (job.payment_status === "awaiting_payment" || job.payment_status === "overdue") && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); setMarkPaidOpen(true); }}
              className="h-8 text-xs font-bold gap-1 bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100"
              data-testid={`mark-paid-btn-${job.id}`}
            >
              <HandCoins className="h-3 w-3" /> Mark as Paid
            </Button>
            {latestApproval && latestApproval.decision === "rejected" && (
              <span
                className="inline-flex items-center gap-1 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5"
                title={`Rejected: ${latestApproval.rejection_reason || "—"}`}
                data-testid={`rejection-pill-${job.id}`}
              >
                <AlertTriangle className="h-3 w-3" /> Rejected — see card
              </span>
            )}
          </div>
        )}
        {job.payment_status === "pending_manager_approval" && (
          <AwaitingApprovalPill approval={latestApproval || { booking_id: job.id, marked_at: new Date().toISOString() }} />
        )}
        {job.scope_change_flag && !job.scope_change_acknowledged && (
          <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 inline-flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> Scope change pending
          </div>
        )}
        {/* Phase P.11 — Mark Completed CTA + completed banner. Phase P.28 —
            teal palette, taller touch target. */}
        {!isPastDay && (job.status || "").toLowerCase() !== "completed" && (job.status || "").toLowerCase() !== "cancelled" && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); markCompleted(); }}
              disabled={completing}
              className="min-h-[44px] px-4 text-sm font-bold gap-1.5 rounded-xl text-white"
              style={{ backgroundColor: "#03313A" }}
              data-testid={`mark-completed-btn-${job.id}`}
            >
              {completing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Mark Completed
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); setProductsOpen(true); }}
              className="min-h-[44px] px-3 text-sm font-bold gap-1.5 rounded-xl border-[#03313A]/20 text-[#03313A] hover:bg-[#8FFFE0]/25"
              data-testid={`add-product-btn-${job.id}`}
            >
              + Product
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); setAddExtraOpen(true); }}
              className="min-h-[44px] px-3 text-sm font-bold gap-1.5 rounded-xl border-[#03313A]/20 text-[#03313A] hover:bg-[#8FFFE0]/25"
              data-testid={`add-extra-service-btn-${job.id}`}
            >
              + Extra service
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); setProposeOpen(true); }}
              className="min-h-[44px] px-3 text-sm font-bold gap-1.5 rounded-xl border-[#03313A]/20 text-[#03313A] hover:bg-[#8FFFE0]/25"
              data-testid={`suggest-template-btn-${job.id}`}
            >
              + Template
            </Button>
          </div>
        )}
        {(job.status || "").toLowerCase() === "completed" && (
          <div
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5"
            data-testid={`completed-banner-${job.id}`}
          >
            <CheckCircle2 className="h-3 w-3" />
            Completed {job.completed_at ? formatBookingStamp(job.completed_at) : ""}
            {job.completed_by_name ? ` by ${job.completed_by_name}` : ""}
          </div>
        )}
        <div className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1 font-semibold">
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {expanded ? "Hide details" : "Show details"}
        </div>
        </div>{/* /BOTTOM BLOCK */}
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-[#03313A]/10 pt-4 space-y-4 bg-[#F4F7F8]/30">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="space-y-1">
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Customer</div>
              <div>{job.customer_name}</div>
              {job.customer_contact && <div className="text-muted-foreground">{job.customer_contact}</div>}
              {job.customer_phone && (
                <a href={`tel:${job.customer_phone}`} className="text-accent inline-flex items-center gap-1"><Phone className="h-3 w-3" />{job.customer_phone}</a>
              )}
            </div>
            {requireLocation && (
              <div className="space-y-1">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Location</div>
                {loc.address_line && (
                  <div
                    className="whitespace-pre-wrap select-text cursor-text"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`staff-job-address-${job.id}`}
                  >{linkifyText(loc.address_line)}</div>
                )}
                {postcode && <div className="mono text-xs">{postcode}</div>}
                {mapsHref && (
                  <a href={mapsHref} target="_blank" rel="noreferrer" className="text-accent inline-flex items-center gap-1"><MapPin className="h-3 w-3" />Open in Maps</a>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2" data-testid={`staff-services-${job.id}`}>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Services</div>
            {(job.line_items && job.line_items.length > 0 ? job.line_items : [{
              id: "legacy", description: job.template_snapshot?.name, unit_price: job.template_snapshot?.price,
              duration_minutes: job.scheduled_duration_minutes, quantity: 1,
            }]).map((li, i) => (
              <div key={li.id || i} className="flex items-center gap-3 text-sm flex-wrap border border-border rounded-[6px] px-3 py-2" data-testid={`staff-service-${job.id}-${i}`}>
                <span className="flex items-center gap-1 flex-1 min-w-0"><Tag className="h-3 w-3 shrink-0" /><span className="truncate">{li.description}{li.quantity > 1 ? ` ×${li.quantity}` : ""}</span></span>
                <span className="text-[11px] text-muted-foreground">{(Number(li.duration_minutes) || 0) * Number(li.quantity || 1)}m</span>
                <span className="flex items-center gap-1 font-bold mono"><Coins className="h-3 w-3" />{formatCurrency((Number(li.unit_price) || 0) * Number(li.quantity || 1), currency)}</span>
              </div>
            ))}
            <div className="pt-1">
              {/* Phase 21.1 — large always-visible payment badge in expanded view. */}
              <PaymentBadge job={job} size="lg" />
            </div>
          </div>

          {job.notes && (
            <div
              className="bg-yellow-100 border-l-4 border-yellow-400 rounded px-3 py-2"
              data-testid={`staff-job-notes-${job.id}`}
            >
              <div className="font-bold text-slate-900 text-sm">Notes</div>
              <div className="text-slate-800 text-sm whitespace-pre-line mt-1 select-text cursor-text">{linkifyText(job.notes)}</div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">My notes</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes} data-testid={`staff-notes-${job.id}`} />
            <div className="text-[11px] text-muted-foreground">Saves on blur.</div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Photos</Label>
            <div className="flex flex-wrap gap-2">
              {job.staff_photos?.map((p) => (
                <a key={p.filename} href={buildAssetUrl(p.url)} target="_blank" rel="noreferrer">
                  <img src={buildAssetUrl(p.url)} alt="" className="h-20 w-20 object-cover rounded-[6px] border border-border" />
                </a>
              ))}
              <input type="file" accept="image/*" capture="environment" ref={fileRef} className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid={`staff-photo-input-${job.id}`} />
              <button type="button" onClick={() => fileRef.current?.click()} className="h-20 w-20 rounded-[6px] border border-dashed border-border grid place-items-center text-muted-foreground hover:border-accent hover:text-accent transition-colors" data-testid={`staff-add-photo-${job.id}`}>
                <Camera className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Payment status</Label>
            <Select value={paymentStatus} onValueChange={updatePayment}>
              <SelectTrigger data-testid={`staff-payment-select-${job.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_STATUSES.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="border-t border-border pt-3">
            <Dialog open={scopeOpen} onOpenChange={setScopeOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-rose-700 hover:text-rose-700" data-testid={`flag-scope-button-${job.id}`}>
                  <AlertTriangle className="h-3 w-3 mr-1" /> Flag scope change
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="scope-dialog">
                <DialogHeader>
                  <DialogTitle>Flag scope change</DialogTitle>
                  <DialogDescription>Tell the manager what's different. They'll get a notification.</DialogDescription>
                </DialogHeader>
                <Textarea rows={4} placeholder="What changed..." value={scopeNote} onChange={(e) => setScopeNote(e.target.value)} data-testid="scope-note-input" />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setScopeOpen(false)}>Cancel</Button>
                  <Button onClick={flagScope} disabled={submittingScope} className="btn-cta" data-testid="scope-submit-button">
                    {submittingScope ? "Sending..." : "Send to manager"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Phase 20 — Chat with manager office. Polled every 10s while expanded. */}
          <BookingChat
            bookingId={job.id}
            side="staff"
            apiClient={staffApi}
            label={`Chat with ${companyName || "office"}`}
            emptyHint="No messages yet. Reach out if anything's unclear."
          />
          {/* Phase 32 — Inline rejection reason banner (visible only when
              the latest approval was rejected). Stays visible until the
              staff re-submits a new mark-paid request. */}
          {latestApproval?.decision === "rejected" && latestApproval.rejection_reason && (
            <div className="border border-rose-200 bg-rose-50 rounded-[6px] p-3 text-sm" data-testid={`rejection-reason-banner-${job.id}`}>
              <div className="mono text-[10px] uppercase tracking-widest text-rose-700 mb-1">Manager rejected the previous mark-paid</div>
              <div className="text-rose-900">{latestApproval.rejection_reason}</div>
            </div>
          )}
        </div>
      )}
      {/* Phase 32 — Mark-as-Paid dialog mounted OUTSIDE the expandable
          button so it doesn't toggle the panel when opened. */}
      <MarkPaidDialog
        job={job}
        currency={currency}
        open={markPaidOpen}
        onOpenChange={setMarkPaidOpen}
        onMarked={() => { onChanged?.(); }}
      />
      {/* Phase P.11 — Post-complete "Send invoice now?" popup. Only opens
          when the server flagged this booking's payment_type as one that
          triggers the prompt (VAT / RCT / custom in-books non-cash). */}
      <Dialog
        open={!!postCompletePrompt}
        onOpenChange={(o) => { if (!o) dismissPromptLater(); }}
      >
        <DialogContent data-testid={`send-invoice-prompt-${job.id}`}>
          <DialogHeader>
            <DialogTitle>Send invoice to customer now?</DialogTitle>
            <DialogDescription>
              This job uses{" "}
              <span className="mono font-semibold">
                {postCompletePrompt?.payment_type_name || "an invoice-generating payment type"}
              </span>
              . Send it now, or the manager can send it later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={dismissPromptLater}
              disabled={sendingInvoice}
              data-testid={`send-invoice-later-${job.id}`}
            >
              Later
            </Button>
            <Button
              onClick={sendInvoiceNow}
              disabled={sendingInvoice}
              className="btn-cta"
              data-testid={`send-invoice-now-${job.id}`}
            >
              {sendingInvoice ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              Send invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase P.16 — pre-complete: products reminder then signature. */}
      {preCompleteStep === "products" && (
        <StaffProductsDialog
          bookingId={job.id}
          onClose={() => {
            // Skip → try signature next; if signature not needed, complete.
            setPreCompleteStep(null);
            (async () => {
              try {
                const { data: probe } = await staffApi.get(
                  `/staff-portal/bookings/${job.id}/pre-complete-check`,
                );
                if (probe?.prompt_signature && !probe?.signature_present) {
                  setPreCompleteStep("signature");
                  return;
                }
              } catch (_) { /* fall through */ }
              _doFinalComplete();
            })();
          }}
        />
      )}
      {preCompleteStep === "signature" && (
        <StaffSignatureDialog
          bookingId={job.id}
          onDone={() => {
            setPreCompleteStep(null);
            _doFinalComplete();
          }}
          onSkipConfirmed={() => {
            setPreCompleteStep(null);
            _doFinalComplete();
          }}
        />
      )}

      {/* Phase P.15 — Products used dialog */}
      {productsOpen && (
        <StaffProductsDialog
          bookingId={job.id}
          onClose={() => setProductsOpen(false)}
        />
      )}
      {/* Phase P.15 — Template proposal dialog */}
      {proposeOpen && (
        <StaffTemplateProposalDialog
          onClose={() => setProposeOpen(false)}
        />
      )}
      {/* Phase P.17 — Add existing template (extra service) to this booking */}
      {addExtraOpen && (
        <StaffAddExtraServiceDialog
          bookingId={job.id}
          existingTemplateIds={(job.line_items || []).map((li) => li.template_id).filter(Boolean)}
          currency={currency}
          onClose={() => setAddExtraOpen(false)}
          onAdded={() => { setAddExtraOpen(false); onChanged?.(); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Phase P.15 — Staff Portal · Products used on this booking
// ============================================================================
function StaffProductsDialog({ bookingId, onClose }) {
  const [products, setProducts] = useState([]);
  const [usages, setUsages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Phase P.33 — simplified staff toggle: "charge" (bills the customer at
  // the booking's payment_type VAT/RCT/cash rules) or "foc" (freebie —
  // stock still deducts but nothing appears on the invoice).
  const [pick, setPick] = useState({ product_id: "", quantity: 1, billing_mode: "charge" });

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, uRes] = await Promise.all([
        staffApi.get("/staff-portal/my-stock"),
        staffApi.get(`/staff-portal/bookings/${bookingId}/products`).catch(() => ({ data: [] })),
      ]);
      // `my-stock` returns rows joined with the product name/price. Map into
      // the same shape the picker expects so the render code stays untouched.
      setProducts((pRes.data || []).map((r) => ({
        id: r.product_id,
        name: r.product_name,
        sku: r.product_sku,
        unit_price_cents: r.unit_price_cents,
        qty_on_hand: r.qty_on_hand,
      })));
      setUsages(uRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const add = async () => {
    if (!pick.product_id) { toast.error("Pick a product first"); return; }
    setBusy(true);
    try {
      await staffApi.post(`/staff-portal/bookings/${bookingId}/products`, {
        product_id: pick.product_id,
        quantity: Number(pick.quantity) || 1,
        billing_mode: pick.billing_mode,
      });
      toast.success("Product added");
      setPick({ product_id: "", quantity: 1, billing_mode: "charge" });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (usageId) => {
    // Phase P.27.1 — shadcn AlertDialog via `removeConfirm` state (declared
    // below) replaces the native `window.confirm` for a consistent look.
    setRemoveConfirm(usageId);
  };
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const doRemove = async (usageId) => {
    try {
      await staffApi.delete(`/staff-portal/bookings/${bookingId}/products/${usageId}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setRemoveConfirm(null); }
  };

  const picked = products.find((p) => p.id === pick.product_id);
  const lowStock = picked && Number(pick.quantity) > picked.qty_on_hand;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="staff-products-dialog">
        <DialogHeader>
          <DialogTitle>Products used</DialogTitle>
          <DialogDescription>Attach parts to this job — charge the customer or add as a freebie (FOC).</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <>
            <div className="space-y-2">
              {usages.length === 0 ? (
                <div className="text-sm text-muted-foreground italic">No products attached yet.</div>
              ) : (
                usages.map((u) => (
                  <div key={u.id} className="border border-border rounded-md p-2 flex items-center gap-2 text-sm" data-testid={`usage-row-${u.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">{u.quantity}× {u.product_name_snapshot}</div>
                      <div className="text-[11px] text-muted-foreground">
                        €{(u.unit_price_cents_at_use / 100).toFixed(2)} each · {(u.billing_mode === "foc" || u.billing_mode === "off_the_books") ? "FOC" : "Charged"}
                      </div>
                    </div>
                    <button
                      className="text-rose-700 text-xs font-bold"
                      onClick={() => remove(u.id)}
                      data-testid={`usage-remove-${u.id}`}
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="border-t border-border pt-3 mt-3 space-y-2">
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Add product</div>
              <select
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm"
                value={pick.product_id}
                onChange={(e) => setPick({ ...pick, product_id: e.target.value })}
                data-testid="staff-product-picker"
              >
                <option value="">— Pick a product —</option>
                {products.filter((p) => (p.qty_on_hand || 0) > 0).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.sku ? ` (${p.sku})` : ""} · €{(p.unit_price_cents / 100).toFixed(2)} · you have {p.qty_on_hand}
                  </option>
                ))}
              </select>
              {products.filter((p) => (p.qty_on_hand || 0) > 0).length === 0 && (
                <div className="text-xs text-muted-foreground italic">
                  You have no stock. Ask your manager to hand you products first.
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Quantity</Label>
                  <Input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={pick.quantity}
                    onChange={(e) => setPick({ ...pick, quantity: e.target.value })}
                    data-testid="staff-product-qty"
                  />
                </div>
                <div>
                  <Label className="text-xs">Billing</Label>
                  {/* Phase P.33 — simple FOC / Charge segmented toggle. */}
                  <div
                    className="flex rounded-md border border-border overflow-hidden text-sm"
                    role="radiogroup"
                    aria-label="Product billing mode"
                    data-testid="staff-product-mode"
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={pick.billing_mode === "foc"}
                      onClick={() => setPick({ ...pick, billing_mode: "foc" })}
                      className={
                        "flex-1 py-2 font-semibold transition-colors " +
                        (pick.billing_mode === "foc"
                          ? "bg-amber-500 text-white"
                          : "bg-background text-muted-foreground hover:bg-muted")
                      }
                      data-testid="staff-product-mode-foc"
                    >
                      FOC
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={pick.billing_mode === "charge"}
                      onClick={() => setPick({ ...pick, billing_mode: "charge" })}
                      className={
                        "flex-1 py-2 font-semibold transition-colors border-l border-border " +
                        (pick.billing_mode === "charge"
                          ? "bg-emerald-600 text-white"
                          : "bg-background text-muted-foreground hover:bg-muted")
                      }
                      data-testid="staff-product-mode-charge"
                    >
                      Charge
                    </button>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {pick.billing_mode === "foc"
                      ? "Freebie — stock still deducts, not on invoice."
                      : "Added to invoice at this job\u2019s payment type."}
                  </div>
                </div>
              </div>
              {lowStock && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  ⚠ You only have {picked.qty_on_hand} of {picked.name}. Server will block save.
                </div>
              )}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={add} disabled={busy || !pick.product_id} data-testid="staff-product-save">
            {busy ? "Adding…" : "Add product"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <ConfirmDialog
        state={removeConfirm && {
          title: "Remove product usage?",
          description: "This removes the part from the job — the stock is returned to inventory.",
          confirmLabel: "Remove",
          destructive: true,
          testId: "staff-product-remove-confirm",
          onConfirm: () => doRemove(removeConfirm),
        }}
        onOpenChange={() => setRemoveConfirm(null)}
      />
    </Dialog>
  );
}

// ============================================================================
// Phase P.15 — Staff Portal · Template proposal
// ============================================================================
function StaffTemplateProposalDialog({ onClose }) {
  const [f, setF] = useState({ name: "", description: "", unit_price_cents: "", tax_rate: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await staffApi.post("/staff-portal/template-proposals", {
        name: f.name.trim(),
        description: f.description,
        unit_price_cents: f.unit_price_cents ? Math.round(Number(f.unit_price_cents)) : null,
        tax_rate: f.tax_rate !== "" ? Number(f.tax_rate) : null,
        reason: f.reason,
      });
      toast.success("Suggestion sent to your manager");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="staff-template-proposal-dialog">
        <DialogHeader>
          <DialogTitle>Suggest a new template</DialogTitle>
          <DialogDescription>
            Job scope doesn&apos;t fit any existing template? Send an idea to your manager — they&apos;ll approve or reject it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div>
            <Label>Template name</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Pump Hire + Emergency Callout" data-testid="proposal-name" />
          </div>
          <div>
            <Label>What does it include?</Label>
            <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Optional — short description" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Suggested price (cents)</Label>
              <Input type="number" value={f.unit_price_cents} onChange={(e) => setF({ ...f, unit_price_cents: e.target.value })} placeholder="Optional" />
            </div>
            <div>
              <Label>Tax rate</Label>
              <select
                className="w-full px-3 py-2 rounded-md border border-border bg-background"
                value={f.tax_rate}
                onChange={(e) => setF({ ...f, tax_rate: e.target.value })}
              >
                <option value="">—</option>
                <option value="0.23">23%</option>
                <option value="0.135">13.5%</option>
                <option value="0">0%</option>
              </select>
            </div>
          </div>
          <div>
            <Label>Why suggest this?</Label>
            <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Optional — helps the manager decide" data-testid="proposal-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="proposal-submit">
            {busy ? "Sending…" : "Send suggestion"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default StaffPortalRouter;

// ============================================================================
// Phase P.17 — Staff Portal · "My stock" read-only panel
// ----------------------------------------------------------------------------
// Renders the products currently allocated to this staff member. Data source:
// `GET /api/staff-portal/my-stock`. Responsive: table on ≥sm, cards on mobile.
// ============================================================================
function StaffMyStockPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await staffApi.get("/staff-portal/my-stock");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Failed to load stock");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
    } catch { return "—"; }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="staff-stock-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your stock…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="staff-stock-error">
        {error}
        <Button size="sm" variant="outline" className="ml-3 h-7" onClick={load}>Retry</Button>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div
        className="text-sm text-muted-foreground italic bg-secondary/30 border border-dashed border-border rounded-[6px] px-4 py-6 text-center"
        data-testid="staff-stock-empty"
      >
        You have no products allocated. Ask your manager to hand you stock.
      </div>
    );
  }

  return (
    <div data-testid="staff-stock-panel">
      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
        Products allocated to you
      </div>
      {/* Mobile: card list. */}
      <div className="sm:hidden space-y-2">
        {rows.map((r) => (
          <div
            key={r.id || r.product_id}
            className="bg-white border border-[#03313A]/10 rounded-2xl p-4 flex items-start gap-3 shadow-sm"
            data-testid={`staff-stock-row-${r.product_id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{r.product_name || "—"}</div>
              {r.product_sku && (
                <div className="mono text-[11px] text-muted-foreground">{r.product_sku}</div>
              )}
              <div className="text-[11px] text-muted-foreground mt-1">
                Last hand-out: <span className="font-medium text-foreground">{fmtDate(r.updated_at)}</span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Qty</div>
              <div className="font-bold text-lg" data-testid={`staff-stock-qty-${r.product_id}`}>
                {Number(r.qty_on_hand || 0)}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Desktop: table. */}
      <div className="hidden sm:block bg-card border border-border rounded-[8px] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left">
              <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground font-normal">Product</th>
              <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground font-normal">SKU</th>
              <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground font-normal text-right">Qty on hand</th>
              <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground font-normal">Last hand-out</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id || r.product_id} className="border-t border-border" data-testid={`staff-stock-row-${r.product_id}`}>
                <td className="px-3 py-2 font-medium">{r.product_name || "—"}</td>
                <td className="px-3 py-2 mono text-[12px] text-muted-foreground">{r.product_sku || "—"}</td>
                <td className="px-3 py-2 text-right font-bold" data-testid={`staff-stock-qty-${r.product_id}`}>
                  {Number(r.qty_on_hand || 0)}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{fmtDate(r.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Phase P.17 — Staff Portal · Add existing template ("+ Extra service")
// ----------------------------------------------------------------------------
// Lets the staff append an EXISTING tenant template to an active booking.
// Backend snapshots price/duration at the point of append — we don't allow
// custom price overrides here (that's a manager concern).
// ============================================================================
function StaffAddExtraServiceDialog({ bookingId, existingTemplateIds = [], currency = "EUR", onClose, onAdded }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await staffApi.get("/staff-portal/templates");
        if (cancelled) return;
        setTemplates(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e?.response?.data?.detail) || "Failed to load templates");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const alreadyOnBooking = new Set(existingTemplateIds || []);
  const available = templates.filter((t) => !alreadyOnBooking.has(t.id));
  const picked = templates.find((t) => t.id === templateId);

  const submit = async () => {
    if (!templateId) { toast.error("Pick a template first"); return; }
    const qty = Number(quantity);
    if (!qty || qty <= 0) { toast.error("Quantity must be greater than 0"); return; }
    setBusy(true);
    try {
      await staffApi.post(`/staff-portal/bookings/${bookingId}/add-template`, {
        template_id: templateId,
        quantity: qty,
      });
      toast.success(`Added ${picked?.name || "extra service"} to job`);
      onAdded?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="staff-add-extra-dialog">
        <DialogHeader>
          <DialogTitle>Add extra service</DialogTitle>
          <DialogDescription>
            Append an existing template (e.g. Extra Load, Delay Fee) to this job. Price and duration are snapshotted from the template.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="staff-add-extra-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
          </div>
        ) : error ? (
          <div className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {error}
          </div>
        ) : available.length === 0 ? (
          <div
            className="text-sm text-muted-foreground italic bg-secondary/30 border border-dashed border-border rounded-md px-3 py-4 text-center"
            data-testid="staff-add-extra-empty"
          >
            No more templates available — every active template is already on this booking.
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Template</Label>
              <div
                className="mt-1 max-h-[240px] overflow-y-auto border border-border rounded-md divide-y divide-border"
                data-testid="staff-add-extra-template-list"
                role="radiogroup"
                aria-label="Available templates"
              >
                {available.map((t) => {
                  const selected = t.id === templateId;
                  return (
                    <label
                      key={t.id}
                      className={`flex items-start gap-3 px-3 py-2 cursor-pointer text-sm transition-colors ${selected ? "bg-accent/10" : "hover:bg-secondary/40"}`}
                      data-testid={`staff-add-extra-template-${t.id}`}
                    >
                      <input
                        type="radio"
                        name="staff-add-extra-template"
                        value={t.id}
                        checked={selected}
                        onChange={() => setTemplateId(t.id)}
                        className="mt-1"
                        data-testid={`staff-add-extra-template-radio-${t.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{t.name}</div>
                        {t.description && (
                          <div className="text-[11px] text-muted-foreground line-clamp-2">{t.description}</div>
                        )}
                        <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                          <span className="inline-flex items-center gap-1"><Coins className="h-3 w-3" />{formatCurrency(Number(t.default_price) || 0, currency)}</span>
                          <span className="inline-flex items-center gap-1"><Timer className="h-3 w-3" />{t.default_duration_minutes || 0}m</span>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
              {alreadyOnBooking.size > 0 && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  {alreadyOnBooking.size} template{alreadyOnBooking.size > 1 ? "s" : ""} already on this booking hidden.
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs">Quantity</Label>
              <Input
                type="number"
                min="0.1"
                step="0.1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                data-testid="staff-add-extra-qty"
              />
            </div>

            {picked && (
              <div className="text-xs bg-secondary/40 border border-border rounded-md px-3 py-2">
                Line total preview:{" "}
                <span className="font-bold mono">
                  {formatCurrency((Number(picked.default_price) || 0) * (Number(quantity) || 0), currency)}
                </span>
                <span className="text-muted-foreground"> · {(Number(picked.default_duration_minutes) || 0) * (Number(quantity) || 0)}m</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || loading || !templateId || available.length === 0}
            className="btn-cta"
            data-testid="staff-add-extra-submit"
          >
            {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Adding…</> : "Add to job"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Phase P.16 — Customer signature capture dialog (canvas + skip sub-dialog).
// ============================================================================
function StaffSignatureDialog({ bookingId, onDone, onSkipConfirmed }) {
  // Phase P.29 (v3) — callback ref instead of useRef. Radix Dialog Portal
  // mounts its children asynchronously so useLayoutEffect fires with
  // canvasRef.current === null (see iteration_29 test report). A callback
  // ref runs the moment the <canvas> is attached to the DOM, before any
  // effect gets a chance, so we're guaranteed a non-null element.
  const canvasRef = useRef(null);
  const applySizeRef = useRef(() => false);
  const rafTickRef = useRef(0);
  const [signedName, setSignedName] = useState("");
  const [hasStrokes, setHasStrokes] = useState(false);
  const [busy, setBusy] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipAck, setSkipAck] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const drawingRef = useRef({ down: false, last: null });

  // Build the sizing helper once. Uses `canvasRef.current` at call-time so
  // it works whether the callback ref or a resize event fires it.
  useEffect(() => {
    const H = 160;
    const applyCanvasSize = () => {
      const c = canvasRef.current;
      if (!c) return false;
      const dpr = window.devicePixelRatio || 1;
      const measured = c.clientWidth || c.getBoundingClientRect().width || 0;
      const cssW = Math.max(1, Math.round(measured));
      if (cssW < 8) return false;
      if (c.width === Math.round(cssW * dpr) && c.height === Math.round(H * dpr)) return true;
      let snapshot = null;
      if (c.width > 0 && c.height > 0) {
        try { snapshot = c.toDataURL("image/png"); } catch { /* noop */ }
      }
      c.width = Math.round(cssW * dpr);
      c.height = Math.round(H * dpr);
      const ctx = c.getContext("2d");
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, cssW, H);
      ctx.strokeStyle = "#111827";
      ctx.lineWidth = 2.2;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      if (snapshot) {
        const img = new Image();
        img.onload = () => { ctx.drawImage(img, 0, 0, cssW, H); };
        img.src = snapshot;
      }
      return true;
    };
    applySizeRef.current = applyCanvasSize;
    const onWin = () => applyCanvasSize();
    window.addEventListener("resize", onWin);
    window.addEventListener("orientationchange", onWin);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("orientationchange", onWin);
    };
  }, []);

  // Callback ref — runs the moment React attaches the <canvas> to the DOM.
  // This IS the reliable "mount" event even inside a portal, so we kick off
  // the rAF retry loop + ResizeObserver here rather than in useLayoutEffect.
  const attachCanvas = (node) => {
    canvasRef.current = node;
    if (!node) return;
    let frames = 0;
    const tick = () => {
      frames += 1;
      const ok = applySizeRef.current && applySizeRef.current();
      if (!ok && frames < 30) {
        rafTickRef.current = requestAnimationFrame(tick);
      }
    };
    if (rafTickRef.current) cancelAnimationFrame(rafTickRef.current);
    rafTickRef.current = requestAnimationFrame(tick);
    if (typeof ResizeObserver !== "undefined") {
      try {
        const ro = new ResizeObserver(() => { if (applySizeRef.current) applySizeRef.current(); });
        ro.observe(node);
      } catch { /* older browsers */ }
    }
  };

  // Convert a pointer event to canvas-CSS coordinates. `getBoundingClientRect`
  // already yields the on-screen box in CSS pixels; because the drawing
  // context is scaled by dpr above we DO NOT multiply by dpr here.
  const pt = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const src = e.touches && e.touches[0] ? e.touches[0]
              : e.changedTouches && e.changedTouches[0] ? e.changedTouches[0]
              : e;
    return { x: src.clientX - r.left, y: src.clientY - r.top };
  };
  const start = (e) => {
    // Phase P.29 — defensive re-size at pointerdown. If the effect's rAF
    // loop somehow missed the settle window, one last chance to align the
    // buffer to the current CSS box before we start drawing.
    if (typeof applySizeRef.current === "function") applySizeRef.current();
    drawingRef.current.down = true; drawingRef.current.last = pt(e);
  };
  const move = (e) => {
    if (!drawingRef.current.down) return;
    e.preventDefault();
    const p = pt(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(drawingRef.current.last.x, drawingRef.current.last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    drawingRef.current.last = p;
    if (!hasStrokes) setHasStrokes(true);
  };
  const end = () => { drawingRef.current.down = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.restore();
    setHasStrokes(false);
  };

  const save = async () => {
    if (!hasStrokes) { toast.error("Please sign first — or use Skip"); return; }
    setBusy(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await staffApi.post(`/staff-portal/bookings/${bookingId}/signature`, {
        signature_data_url: dataUrl,
        signed_by_name: signedName,
      });
      toast.success("Signature captured");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmSkip = async () => {
    if (!skipAck) { toast.error("Tick the checkbox to confirm"); return; }
    setBusy(true);
    try {
      await staffApi.post(`/staff-portal/bookings/${bookingId}/signature/skip`, {
        acknowledged: true,
        reason: skipReason,
      });
      toast.success("Signature skipped — job acknowledged");
      onSkipConfirmed();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open onOpenChange={(o) => { if (!o) onSkipConfirmed(); }}>
        <DialogContent data-testid="signature-dialog">
          <DialogHeader>
            <DialogTitle>Customer signature</DialogTitle>
            <DialogDescription>
              Ask the customer to sign below to confirm the job is completed. Optional.
            </DialogDescription>
          </DialogHeader>
          <div>
            <canvas
              ref={attachCanvas}
              onMouseDown={start}
              onMouseMove={move}
              onMouseUp={end}
              onMouseLeave={end}
              onTouchStart={start}
              onTouchMove={move}
              onTouchEnd={end}
              style={{ width: "100%", height: 160, border: "1px solid #d4d4d8", borderRadius: 8, background: "#fff", touchAction: "none", cursor: "crosshair" }}
              data-testid="signature-canvas"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-muted-foreground">
                {hasStrokes ? "Signed" : "Draw with finger or mouse"}
              </span>
              <button type="button" className="text-xs underline" onClick={clear} data-testid="signature-clear">
                Clear
              </button>
            </div>
            <div className="mt-3">
              <Label className="text-xs">Customer name (optional)</Label>
              <Input value={signedName} onChange={(e) => setSignedName(e.target.value)} placeholder="Type customer name" data-testid="signature-name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSkipOpen(true)} disabled={busy} data-testid="signature-skip">
              Skip
            </Button>
            <Button onClick={save} disabled={busy || !hasStrokes} data-testid="signature-save">
              {busy ? "Saving…" : "Save signature"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {skipOpen && (
        <Dialog open onOpenChange={(o) => { if (!o) setSkipOpen(false); }}>
          <DialogContent data-testid="signature-skip-dialog">
            <DialogHeader>
              <DialogTitle>Skip signature?</DialogTitle>
              <DialogDescription>
                Confirm the job is completed. This will be recorded on the invoice PDF.
              </DialogDescription>
            </DialogHeader>
            <label className="flex items-start gap-2 text-sm p-3 border border-border rounded-md" data-testid="signature-skip-ack-row">
              <input type="checkbox" checked={skipAck} onChange={(e) => setSkipAck(e.target.checked)} data-testid="signature-skip-ack" className="mt-0.5" />
              <span className="font-semibold">I confirm this task is completed</span>
            </label>
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Input value={skipReason} onChange={(e) => setSkipReason(e.target.value)} placeholder="e.g. Customer unavailable to sign" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSkipOpen(false)} disabled={busy}>Back</Button>
              <Button onClick={confirmSkip} disabled={busy || !skipAck} data-testid="signature-skip-confirm">
                {busy ? "Saving…" : "Confirm skip"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
